// #region imports

// --- Angular
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

// --- RxJs
import { Observable, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

// --- 3rd Party
import { TranslateService } from '@ngx-translate/core';
import { retryBackoff } from 'backoff-rxjs';

// --- HS Code
import { environment } from 'src/environments/environment';
import {
  ApplicationError,
  ErrorMessage,
  SurveyUserDetailsModel,
  UpdateStatuses
} from '../../../models';
import { AppService } from '../app.service';
import { UserDemographicsBaseService } from '../profile/user-demographics-base.service';
import { UserDetailsBaseService } from './user-details-base.service';

// #endregion

@Injectable({
  providedIn: 'root'
})
export class UserDetailsService extends UserDetailsBaseService {
  constructor(
    private translate: TranslateService,
    private http: HttpClient,
    private userDemographicsService: UserDemographicsBaseService,
    appService: AppService,
  ) {
    super(appService);
  }

  loadSurveyUserDetails(): void {
    if (this._isLoaded) {
      return;
    }

    const url =
      environment.apiConfig.surveyApiUrl + environment.apiConfig.userDetailsApi;
    this.http
      .get<SurveyUserDetailsModel>(url)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._loadError.bind(this))
      )
      .subscribe(result => {
        if (!result) return;

        this._userDetails$.next(result);
        this.userDemographicsService.loadFromUserDetails(result.demographicValues);
        this._isLoaded = true;
      });
  }

  updateUserDetails(userDetails: SurveyUserDetailsModel): void {
    if (this._latestUpdateStatus$.value === UpdateStatuses.requested ||
      this._latestUpdateStatus$.value === UpdateStatuses.processing) return;

    this._latestUpdateStatus$.next(UpdateStatuses.requested);
    const url =
      environment.apiConfig.surveyApiUrl + environment.apiConfig.userDetailsApi;

    this._latestUpdateStatus$.next(UpdateStatuses.processing);
    this.http.put(url, userDetails).pipe(
      retryBackoff(environment.retryConfig),
      switchMap(() => of(true)),
      catchError(this._updateError.bind(this))
    ).subscribe((isSuccessful: boolean) => {
      if (isSuccessful) {
        this._userDetails$.next(userDetails);
        this._latestUpdateStatus$.next(UpdateStatuses.completeSuccessful);
      } else {
        this._latestUpdateStatus$.next(UpdateStatuses.completeError);
      }
    });
  }

  private _loadError(err: any): Observable<SurveyUserDetailsModel | null> {
    this._handleError(err);
    return of(null);
  }

  private _updateError(err: any): Observable<boolean> {
    this._handleError(err);
    return of(false);
  }

  private _handleError(err: any) {
    this.appService.setLoading(false);

    const error: ApplicationError = ApplicationError.create(
      ErrorMessage.genericErrorCode,
      'UserDetailsService',
      true,
      err
    );

    this.appService.setLastError(error);
  }
}
